import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import EllipsisOverflow from 'components/formatting/ellipsis-overflow';
import Decorator from 'clientDecorator';
import Avatar from 'components/elements/avatar';
import { Link } from '@evertrue/et-components';

const ContactCard = ({
  avatar,
  contact = {},
  name,
  link,
  align = 'middle',
  avatarSize = 40,
  showClassYear = true,
  showRemoteId = false,
  showNameLink = true,
  includeMiddle = false,
  referrer,
  onMouseOver,
  onMouseOut,
  onClick,
  isStacked,
  target = '_self',
  className,
  children,
}) => {
  let parsedContact = contact || {};
  parsedContact =
    _.has(contact, 'customs') || _.has(contact, 'avatar') || _.has(contact, 'lids')
      ? contact
      : Decorator.Contacts.parse(contact);

  const renderNameFromGql = (contact) => {
    return contact?.contact_attributes?.name_full;
  };

  const formattedName = includeMiddle
    ? Decorator.Contacts.getDetailedName(parsedContact, { include_middle: true }) ||
      parsedContact.name ||
      renderNameFromGql(parsedContact)
    : Decorator.Contacts.getDetailedName(parsedContact) || parsedContact.name || renderNameFromGql(parsedContact);

  const isDeceased = parsedContact?.properties?.deceased?.value;
  const avatarUrl = Decorator.Profile.getAvatar(parsedContact, parsedContact.lids) || parsedContact.avatar;

  const renderLink = (component, nameStr) => {
    const contactLink = link || `/contact/${parsedContact?.id}`;
    if (!showNameLink || !parsedContact?.id) return component;

    return (
      <Link href={contactLink} target={target} title={`${nameStr}'s Profile`} data-refer={referrer}>
        {component}
      </Link>
    );
  };

  const remoteId = _.find(parsedContact.identities, { type: 'remote_id' })?.value;

  return (
    <div
      className={classNames('new-contact-card', className)}
      onClick={onClick}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
    >
      <div
        className={classNames('new-contact-card--body', {
          'is-stacked': isStacked,
        })}
      >
        {avatar && _.isFunction(avatar)
          ? avatar(parsedContact.avatar, avatarSize, 'new-contact-card--avatar fs-exclude-data')
          : renderLink(
              <Avatar
                className="new-contact-card--avatar fs-exclude-data"
                name={formattedName}
                url={avatarUrl}
                width={avatarSize}
              />,
              formattedName
            )}

        <div
          className="new-contact-card--content"
          style={{
            width: `calc(100% - ${avatarSize}px)`,
            verticalAlign: align,
          }}
        >
          <div
            className={classNames('new-contact-card--name', {
              'is-deceased': isDeceased && !name,
            })}
          >
            {name
              ? name(formattedName, renderLink(<strong className="fs-exclude-data">{formattedName}</strong>))
              : renderLink(<strong className="fs-exclude-data">{formattedName}</strong>, formattedName)}
            {isDeceased && !name && <span className="new-contact-card--deceased fs-exclude-data">Deceased</span>}
          </div>

          {showClassYear && (
            <EllipsisOverflow className="new-contact-card--class fs-exclude-data">
              {Decorator.Profile.getRoleWithYear(parsedContact)}
            </EllipsisOverflow>
          )}

          {showRemoteId && remoteId && <div className="fs-exclude-data">{`Remote ID: ${remoteId}`}</div>}

          {children && <div className="new-contact-card--children">{children}</div>}
        </div>
      </div>
    </div>
  );
};

ContactCard.propTypes = {
  avatar: PropTypes.func,
  contact: PropTypes.object,
  name: PropTypes.func,
  link: PropTypes.string,
  align: PropTypes.string,
  avatarSize: PropTypes.number,
  showClassYear: PropTypes.bool,
  showRemoteId: PropTypes.bool,
  showNameLink: PropTypes.bool,
  referrer: PropTypes.string,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func,
  onClick: PropTypes.func,
  isStacked: PropTypes.bool,
  target: PropTypes.oneOf(['_blank', '_self']),
  className: PropTypes.string,
  children: PropTypes.node,
  includeMiddle: PropTypes.bool,
};

export default ContactCard;
